/**
 * InboxView — capture + phân loại inbox items.
 * D18: convert qua InboxClarifyModal (không convert nhanh).
 * P2-06: NL quick-add parser → pre-fills clarify modal.
 * P2-10: filter tabs Inbox / Someday / Đang chờ; park actions; stale nudge.
 * UX-B1: per-tab friendly empty states.
 * UX-B5: NL syntax hint popover next to capture input.
 * A7: bulk-clarify queue — "Phân loại" trên Inbox tab mở queue tự động sang mục tiếp.
 */
import { useState, useEffect } from "react";
import { AlertTriangle, Zap, CheckCircle2, ListChecks } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { InboxItem, InboxStatus } from "../types";
import { todayIso } from "../lib/date";
import { parseQuickAdd } from "../lib/quick-add-parser";
import { groupInbox, isInboxStale } from "./inbox-derive";
import { InboxClarifyModal, type ClarifyPrefill, type ClarifyQueueContext } from "./inbox-clarify-modal";
import { NlSyntaxHint } from "./nl-syntax-hint";
import { InboxCard, InboxEmptyState } from "./inbox-card";
import { InboxProcessingModal } from "./inbox-processing-modal";

// ── Tab definition ────────────────────────────────────────────────────────────

type TabKey = InboxStatus;
const TABS: { key: TabKey; label: string }[] = [
  { key: "inbox",   label: "Inbox" },
  { key: "someday", label: "Someday" },
  { key: "waiting", label: "Đang chờ" },
];

// ── Main view ─────────────────────────────────────────────────────────────────

export function InboxView({ api, onDelete }: { api: LifeOsApi; onDelete: (fn: () => void) => void }) {
  const [text, setText] = useState("");
  const [tab, setTab] = useState<TabKey>("inbox");
  const [showHint, setShowHint] = useState(false);

  // P2-06: parse NL text → open clarify modal pre-filled (no silent create).
  // We track the created item's ID (returned by addInboxItem) and DERIVE the item
  // from state — guessing "newest in list" breaks under realtime/multi-tab updates.
  const [pendingPrefill, setPendingPrefill] = useState<ClarifyPrefill | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  // A7: bulk-clarify queue — snapshot of inbox items at session start + current index.
  // queueItems is a frozen snapshot so items removed mid-session don't skip ahead wrong.
  const [queueItems, setQueueItems] = useState<InboxItem[]>([]);
  const [queueIndex, setQueueIndex] = useState(0); // 0-based
  const [inboxZeroToast, setInboxZeroToast] = useState(false);
  const [processingItems, setProcessingItems] = useState<InboxItem[]>([]);

  const today = todayIso();
  const groups = groupInbox(api.state.inbox);
  const stale = isInboxStale(api.state.inbox, today, 3);

  const quickAdd = () => {
    const raw = text.trim();
    if (!raw) return;

    // Always create the inbox item first, then open clarify pre-filled
    const parsed = parseQuickAdd(raw);
    // Add the raw text to inbox (confirmed on clarify submit) — keep its id.
    const createdId = api.actions.addInboxItem({ text: raw });
    setText("");
    setPendingId(createdId);

    // Find the item we just added (it's prepended to the list)
    // We open clarify on a synthetic item representative — the real item will be
    // converted via the modal's convertInboxItem call.
    // Use a small timeout so state updates before we read inbox[0].
    // KISS: open clarify directly with parsed prefill; user confirms before convert.
    const prefill: ClarifyPrefill = {
      title: parsed.title !== raw ? parsed.title : undefined,
      dueDate: parsed.dueDate,
      time: parsed.time,
      priority: parsed.priority,
      tags: parsed.tags.length > 0 ? parsed.tags : undefined,
    };
    setPendingPrefill(prefill);
  };

  // Resolve the pending clarify target by ID — a pure lookup each render, no
  // setState-in-render, immune to list reordering from sync echoes.
  const pendingItem =
    pendingId !== null ? (api.state.inbox.find((item) => item.id === pendingId) ?? null) : null;

  const closePending = () => {
    setPendingPrefill(null);
    setPendingId(null);
  };

  // A7: start a bulk-clarify queue beginning at `startIndex` in `items` snapshot.
  const startQueue = (items: InboxItem[], startIndex: number) => {
    setQueueItems(items);
    setQueueIndex(startIndex);
  };

  // A7: advance to next item; if none remain, close queue and show toast.
  const advanceQueue = () => {
    const next = queueIndex + 1;
    if (next >= queueItems.length) {
      // All items processed — close queue + toast
      setQueueItems([]);
      setQueueIndex(0);
      setInboxZeroToast(true);
    } else {
      setQueueIndex(next);
    }
  };

  const closeQueue = () => {
    setQueueItems([]);
    setQueueIndex(0);
  };

  const startProcessing = () => {
    if (groups.inbox.length === 0) return;
    setProcessingItems(groups.inbox);
  };

  // Auto-dismiss inbox-zero toast after 3 s
  useEffect(() => {
    if (!inboxZeroToast) return;
    const t = setTimeout(() => setInboxZeroToast(false), 3000);
    return () => clearTimeout(t);
  }, [inboxZeroToast]);

  // A7: queue mode is active when queueItems is non-empty and current item still exists in live inbox.
  const queueActive = queueItems.length > 0;
  // Resolve current queue item against live state so we always pass a fresh object.
  const queueCurrentRaw = queueItems[queueIndex];
  const queueCurrentLive = queueCurrentRaw
    ? api.state.inbox.find((i) => i.id === queueCurrentRaw.id) ?? queueCurrentRaw
    : null;
  // Build ClarifyQueueContext (1-based index for display).
  const queueContext: ClarifyQueueContext | undefined = queueActive && queueCurrentLive
    ? { index: queueIndex + 1, total: queueItems.length, onNext: advanceQueue }
    : undefined;

  // Which items to show in the active tab
  const visibleItems =
    tab === "inbox"   ? groups.inbox :
    tab === "someday" ? groups.someday :
    groups.waiting;

  return (
    <div className="mx-auto max-w-2xl">
      {/* Capture bar */}
      <div className="rounded-3xl bg-white p-5">
        <div className="flex items-center gap-3 rounded-2xl border-2 border-brand-100 bg-brand-50 px-4 py-3.5">
          <Zap className="h-5 w-5 text-brand-600" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && quickAdd()}
            className="flex-1 bg-transparent text-[15px] outline-none placeholder:text-ink-500"
            placeholder={'Quăng vào đây… "họp 3pm mai #work !cao" → Enter'}
          />
          {/* B5: NL syntax hint toggle */}
          <div className="relative">
            <button
              onClick={() => setShowHint((v) => !v)}
              aria-label="Xem cú pháp ghi nhanh"
              title="Cú pháp ghi nhanh"
              className={`grid h-7 w-7 place-items-center rounded-lg text-[12px] font-bold transition ${
                showHint
                  ? "bg-brand-100 text-brand-700"
                  : "text-ink-400 hover:bg-ink-100 hover:text-ink-700"
              }`}
            >
              ?
            </button>
            <NlSyntaxHint open={showHint} onClose={() => setShowHint(false)} />
          </div>
          <button onClick={quickAdd} className="gbrand rounded-xl px-3.5 py-2 text-[13px] font-bold text-white">
            Lưu
          </button>
        </div>
        <p className="mt-2 px-1 text-[12px] text-ink-500">Mẹo: thêm thời gian, ngày, ưu tiên (!cao/!tb/!thấp), #tag — modal xác nhận trước khi tạo.</p>
      </div>

      {/* Stale nudge banner */}
      {stale && tab === "inbox" && (
        <div className="mt-3 flex items-center gap-2 rounded-2xl bg-amber-50 px-4 py-2.5 dark:bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
          <p className="text-[12px] text-amber-700 dark:text-amber-300">
            Inbox có mục chờ xử lý trên 3 ngày — dành 5 phút phân loại nhé.
          </p>
        </div>
      )}

      {/* Filter tabs */}
      <div className="mt-4 flex gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`inbox-tab-${t.key}`}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-xl px-3.5 py-1.5 text-[12px] font-bold transition ${
              tab === t.key ? "gbrand text-white" : "bg-white text-ink-600 hover:bg-ink-100"
            }`}
          >
            {t.label}
            <span className="ml-1.5 opacity-60">
              ({t.key === "inbox" ? groups.inbox.length : t.key === "someday" ? groups.someday.length : groups.waiting.length})
            </span>
          </button>
        ))}
      </div>

      {/* List header */}
      <div className="mt-4 flex items-center justify-between px-1">
        <h3 className="font-display text-[16px] font-bold">
          {tab === "inbox"   ? `Chờ xử lý (${groups.inbox.length})` :
           tab === "someday" ? `Someday / Maybe (${groups.someday.length})` :
           `Đang chờ (${groups.waiting.length})`}
        </h3>
        {tab === "inbox" && (
          <button
            onClick={startProcessing}
            disabled={groups.inbox.length === 0}
            className="flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-[12px] font-bold text-brand-600 disabled:opacity-40"
          >
            <ListChecks className="h-3.5 w-3.5" />
            Xử lý tuần tự
          </button>
        )}
      </div>

      {/* Cards */}
      <div className="mt-3 space-y-3">
        {visibleItems.map((it) => (
          <InboxCard
            key={it.id}
            item={it}
            api={api}
            onDelete={onDelete}
            today={today}
            // A7: inbox-tab cards start a queue; someday/waiting use single-item mode (no onStartQueue)
            onStartQueue={tab === "inbox" ? (idx) => startQueue(groups.inbox, idx) : undefined}
            inboxItems={tab === "inbox" ? groups.inbox : undefined}
          />
        ))}
        {visibleItems.length === 0 && <InboxEmptyState tabKey={tab} />}
      </div>

      {/* Pending clarify modal from quick-add */}
      {pendingItem && pendingPrefill && (
        <InboxClarifyModal
          item={pendingItem}
          api={api}
          onClose={closePending}
          prefill={pendingPrefill}
        />
      )}

      {/* A7: bulk-clarify queue modal */}
      {queueActive && queueCurrentLive && (
        <InboxClarifyModal
          key={queueCurrentLive.id}
          item={queueCurrentLive}
          api={api}
          onClose={closeQueue}
          queue={queueContext}
        />
      )}

      {processingItems.length > 0 && (
        <InboxProcessingModal
          api={api}
          items={processingItems}
          onClose={() => setProcessingItems([])}
          onDone={() => setInboxZeroToast(true)}
        />
      )}

      {/* A7: inbox-zero toast */}
      {inboxZeroToast && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 flex items-center gap-2 rounded-2xl bg-brand-600 px-5 py-3 text-[13px] font-bold text-white shadow-2xl animate-fade-in">
          <CheckCircle2 className="h-4 w-4" />
          Inbox sạch 🎉
        </div>
      )}
    </div>
  );
}
